import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TriStateCheckBoxComponent, ClickFilterComponent } from '@myapp/features/widgets/components';
import { UiWindowService } from '@myapp/modules/fwcore/services';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { FormFieldModule, TextAreaModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { RequestRoleTemplate, Role, RoleSave, RoleTemplate, RoleType } from '../../../models';
import { AdminService } from '../../../services';
import { NumericTextBoxModule } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'app-roles-editor',
  templateUrl: './roles-editor.component.html',
  styleUrls: ['./roles-editor.component.scss'],
  standalone: true,
  imports: [DialogModule, FormsModule, ReactiveFormsModule, FormFieldModule, LabelModule, TextBoxModule, NumericTextBoxModule, TriStateCheckBoxComponent
    , ClickFilterComponent, NgClass, DropDownListModule, TextAreaModule, NgIf, ButtonModule, AsyncPipe]
})

export class RolesEditorComponent implements AfterViewInit, OnChanges {

  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() editDataItem: Role | null | undefined;

  @Input() public set isReadOnly(item: boolean) {
    if (item) {
      this.editForm.disable();
      this.isReadOnlyCustomControl = true;
    }
    else {
      this.editForm.enable();
      this.isReadOnlyCustomControl = false;
    }
  }

  @Output() cancel: EventEmitter<undefined> = new EventEmitter();
  @Output() save: EventEmitter<RoleSave> = new EventEmitter();

  /*-------------------------- public properties / methods --------------------*/

  protected isReadOnlyCustomControl = true;

  /*-------------------------- Internal properties  ---------------------------*/

  protected loading: boolean = true;
  protected destroy$ = new Subject();

  protected editForm: FormGroup;

  protected roleTypeOptions = Object.keys(RoleType).map(key => ({
    name: RoleType[key as keyof typeof RoleType],
    value: key
  }));

  protected roleTemplateOptions: RoleTemplate[] = [];

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected afUiWin: UiWindowService
    , protected fb: FormBuilder
    , protected adminService: AdminService) {

    this.editForm = this.fb.group({
      name: new FormControl('', Validators.required)
      , roleType: new FormControl('CLI', Validators.required)
      , isActive: new FormControl(true)
      , allLicenseUsers: new FormControl(false)
      , maxUsers: new FormControl(null)
      , description: new FormControl('')
      , templateId: new FormControl(null)
    });
  }

  /*----------------------------------- ng  --------------------------*/

  ngAfterViewInit(): void {
    this.getRoleTemplates();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.editDataItem) {
      this.editForm.patchValue(this.editDataItem as { [key: string]: Role });
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  protected onSave(e: PointerEvent): void {
    e.preventDefault();

    if (this.editForm.valid) {

      let addEditData: RoleSave;

      addEditData = this.editDataItem
        ? { ...this.editDataItem, ...this.editForm.value }
        : this.editForm.value;

      this.save.emit(addEditData);
    }
  }

  protected onCancel(e: PointerEvent): void {
    e.preventDefault();

    this.closeForm();
  }

  /*--------------------------- internal functions  --------------------------*/

  protected getRoleTemplates(): void {

    this.loading = true;

    const req: RequestRoleTemplate = {
      active: true
    };

    this.adminService.getRoleTemplate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe(
        resp => {
          this.roleTemplateOptions = resp.data!;
        }
      );

  }

  protected closeForm(): void {
    this.cancel.emit();
  }

}
